<script lang="ts" setup>
import { computed, ref } from 'vue';
import {
  BUCKET_DATA_TYPES,
  COMPUTE_TASK_TYPES,
  IS_DEBUG_MODE,
  SHARING_USER_ROLES,
} from '@/constants';
import { useUserStore } from '@/store/user';
import { useUploadsStore } from '@/store/uploads';
import { useClipboard } from '@vueuse/core';
import {
  BAlert,
  BButton,
  BButtonGroup,
  BCard,
  BCardText,
  BSpinner,
  BTooltip,
} from 'bootstrap-vue-next';
import { BucketsLiteDocument, ComputeTasksLiteDocument } from '@/gql/graphql';
import { GRAPHQL_ENDPOINT_URL } from '@/apollo/client';
import { KEYCLOAK_URL } from '@/auth/keycloak';
import ComputeTaskList from '@/components/compute/ComputeTaskList.vue';
import BucketList from '@/components/data/BucketList.vue';
import WebUploadList from '@/components/upload/WebUploadList.vue';
import { formatDateTimeToIso } from '@/utils/date-utils';
import { openRouterLink } from '@/utils/router-utils';
import AnalyticsGuide from '@/components/AnalyticsGuide.vue';

const userStore = useUserStore();
const uploadsStore = useUploadsStore();

// --- Component References ---
const dataPipelineListRef = ref<InstanceType<typeof ComputeTaskList>>();
const dataTransferListRef = ref<InstanceType<typeof ComputeTaskList>>();
const webUploadListRef = ref<InstanceType<typeof WebUploadList>>();
const bucketListRef = ref<InstanceType<typeof BucketList>>();
const keycloakAccountUrl =
  process.env.VUE_APP_KEYCLOAK_URL +
  '/realms/' +
  process.env.VUE_APP_KEYCLOAK_REALM +
  '/account';

// --- Component State ---
const showAnalyticsGuideModal = ref(false);
const limitDateTimeRecent = computed(() => {
  const limiteDate = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
  return formatDateTimeToIso(limiteDate);
});

const isLoading = computed(() => {
  return (
    dataPipelineListRef.value?.tasksQueryInProgress ||
    dataTransferListRef.value?.tasksQueryInProgress ||
    bucketListRef.value?.bucketsQueryInProgress
  );
});

const isError = computed(() => {
  return (
    !isLoading.value &&
    (dataPipelineListRef.value?.tasksQueryError ||
      dataTransferListRef.value?.tasksQueryError ||
      bucketListRef.value?.bucketsQueryError ||
      uploadsStore.error)
  );
});

const errorMessage = computed(() => {
  let msg = '';
  if (dataPipelineListRef.value?.tasksQueryError) {
    msg += `Error Fetching Sample Reconstruction Details: ${dataPipelineListRef.value.tasksQueryError.message}\n`;
  }
  if (dataTransferListRef.value?.tasksQueryError) {
    msg += `Error Fetching Data Transfer Details: ${dataTransferListRef.value.tasksQueryError.message}\n`;
  }
  if (bucketListRef.value?.bucketsQueryError) {
    msg += `Error Fetching Recent Data: ${bucketListRef.value.bucketsQueryError.message}\n`;
  }
  return msg;
});

const invitesPending = computed(() => {
  return userStore.pendingInvites > 0;
});

function refetchDashboardDetails() {
  dataPipelineListRef.value?.requestSearch();
  dataTransferListRef.value?.requestSearch();
  bucketListRef.value?.requestSearch();
  // no need to refetch uploads (they are being polled by the app)
}

/* JWT TOKEN DETAILS *************************************************/
function copyUuid() {
  if (userStore.uuid) {
    const clip = useClipboard();
    clip.copy(userStore.uuid);
    showUuidTooltip.value = true;

    // Hide the tooltip after 2 seconds
    setTimeout(() => {
      showUuidTooltip.value = false;
    }, 2000);
  } else {
    alert('UUID not available');
  }
}

function copyAuth() {
  if (userStore.authHeader) {
    const clip = useClipboard();
    clip.copy(userStore.authHeader);
    showAuthTooltip.value = true;

    // Hide the tooltip after 2 seconds
    setTimeout(() => {
      showAuthTooltip.value = false;
    }, 2000);
  } else {
    alert('Authorization details not available');
  }
}

const showUuidTooltip = ref(false);
const showAuthTooltip = ref(false);
</script>

<template>
  <div>
    <h1>
      <font-awesome-icon :icon="['fa', 'home']" />
      Dashboard
    </h1>
    <h5>Welcome to Sampling Human Cloud Analytics!</h5>
    <hr />

    <!-- Pending Invites Alert -->
    <BAlert variant="warning" v-model="invitesPending">
      <h5>
        You have <strong>{{ userStore.pendingInvites }} pending invite</strong>
        {{ userStore.pendingInvites === 1 ? 'request' : 'requests' }} to join
        organizations
      </h5>
      <p>
        Visit the
        <!-- style router-link as BButton of outline-danger variant -->
        <router-link
          :to="{ name: 'OrganizationSettings' }"
          class="text-decoration-none fw-bold"
        >
          organization settings
        </router-link>
        page to accept or decline them.
      </p>
      <b-button
        :to="{ name: 'OrganizationSettings' }"
        variant="warning"
        class="text-decoration-none"
      >
        <font-awesome-icon :icon="['fas', 'users']" class="me-1" />
        Organization Settings
      </b-button>
    </BAlert>

    <!-- Introduction Paragraph -->
    <p>
      Browse through your recent projects, upload data, analyze and inspect
      results. Access premium analysis features. Manage your account and
      organization information.
    </p>

    <!-- Cards -->
    <div class="cards-grid">
      <div class="cards-grid__item">
        <b-card
          title="Analysis in six steps"
          img-src="@/assets/images/card-user-guide.webp"
          img-top
          class="dashboard-card"
          body-class="d-flex flex-column"
          footer-class="mt-auto"
        >
          <b-card-text>
            <ol>
              <li>
                Extract DNA/RNA with a Sampling Human kit and save the kit code.
              </li>
              <li>
                Import sequencing data using the
                <router-link :to="{ name: 'UploadManager' }"
                  >Upload Manager</router-link
                >.
              </li>
              <li>
                Annotate samples in
                <router-link :to="{ name: 'DataManager' }"
                  >Data Manager</router-link
                >.
              </li>
              <li>
                Create a
                <router-link :to="{ name: 'Projects' }">Project</router-link>
                and associate samples.
              </li>
              <li>
                Launch Sample Reconstruction from the
                <router-link :to="{ name: 'Projects' }">Project</router-link>.
              </li>
              <li>
                Review and download results in the
                <router-link :to="{ name: 'Projects' }">Project</router-link>.
              </li>
            </ol>
          </b-card-text>
          <template #footer>
            <b-button
              @click="showAnalyticsGuideModal = true"
              variant="outline-dark"
              size="sm"
              class="w-100"
            >
              <font-awesome-icon icon="book" class="me-1" />
              Read the Full Instructions
            </b-button>
          </template>
        </b-card>
      </div>

      <div class="cards-grid__item">
        <b-card
          title="Upload Manager"
          img-src="@/assets/images/card-upload.webp"
          img-top
          class="dashboard-card"
          body-class="d-flex flex-column"
          footer-class="mt-auto"
        >
          <b-card-text>
            <p>
              Upload FASTQs directly or transfer directly from your sequencing
              provider via FTP.
            </p>
          </b-card-text>
          <template #footer>
            <b-button
              @click="
                openRouterLink({ name: 'UploadManager' }, $router, $event)
              "
              variant="outline-dark"
              size="sm"
              class="w-100"
            >
              <font-awesome-icon icon="upload" class="me-1" />
              Go to Upload Manager
            </b-button>
          </template>
        </b-card>
      </div>

      <div class="cards-grid__item">
        <b-card
          title="Data Management"
          img-src="@/assets/images/card-data.webp"
          img-alt="Data"
          img-top
          class="dashboard-card"
          body-class="d-flex flex-column"
          footer-class="mt-auto"
        >
          <b-card-text>
            <p>
              Browse and annotate samples and share them between projects to get
              the most out of your data.
            </p>
          </b-card-text>
          <template #footer>
            <b-button
              @click="openRouterLink({ name: 'DataManager' }, $router, $event)"
              variant="outline-dark"
              size="sm"
              class="w-100"
            >
              <font-awesome-icon icon="database" class="me-1" />
              Go to Data Manager
            </b-button>
          </template>
        </b-card>
      </div>

      <div class="cards-grid__item">
        <b-card
          title="Cloud Analysis"
          img-src="@/assets/images/card-analytics.webp"
          img-alt="Analytics"
          img-top
          class="dashboard-card"
          body-class="d-flex flex-column"
          footer-class="mt-auto"
        >
          <b-card-text>
            <p>
              Powerful analysis of transcriptomic data at your fingertips.
              Organize your projects, analyze and inspect results.
            </p>
          </b-card-text>
          <template #footer>
            <b-button
              @click="openRouterLink({ name: 'Projects' }, $router, $event)"
              variant="outline-dark"
              size="sm"
              class="w-100"
            >
              <font-awesome-icon icon="list" class="me-1" />
              Go to Projects
            </b-button>
          </template>
        </b-card>
      </div>
    </div>
    <!-- End of Card Deck -->

    <!-- Loading Dashboard spinner -->
    <b-alert v-model="isLoading" variant="secondary" class="mt-4">
      Loading data <b-spinner label="Loading..." small />
    </b-alert>

    <!-- Error & Retry -->
    <b-alert v-model="isError" variant="danger" class="mt-4">
      {{ errorMessage }}
      <b-button
        @click="refetchDashboardDetails"
        variant="outline-dark"
        class="btn-sm"
      >
        <font-awesome-icon icon="sync" />
        Retry
      </b-button>
    </b-alert>

    <!-- Data & Tasks Query Section-->
    <div :class="[{ 'd-none': isLoading }]">
      <!-- Recent Data Pipelines -->
      <div :class="[{ 'd-none': !dataPipelineListRef?.tasks.length }]">
        <h4 class="mt-4">
          <font-awesome-icon :icon="['fa', 'line-chart']" />
          Recent sample reconstructions
        </h4>
        <ComputeTaskList
          ref="dataPipelineListRef"
          class="mb-1"
          parent-name="Dashboard"
          :user-role="SHARING_USER_ROLES.OWNER"
          :task-types="[COMPUTE_TASK_TYPES.DATA_PIPELINE]"
          :updated-after="limitDateTimeRecent"
          :paging-items-per-page="5"
          :paging-hide-controls="true"
          :show-bucket-button="false"
          :show-exec-details="false"
          :show-abort-button="false"
          :message-no-items-found="'No samples reconstructed recently.'"
          :transparent-style="true"
          :query-document="ComputeTasksLiteDocument"
          task-name-column-title="Sample Reconstruction"
        />
        <b-button
          @click="openRouterLink({ name: 'Projects' }, $router, $event)"
          variant="outline-dark"
          size="sm"
          class="mb-2"
        >
          <font-awesome-icon icon="diagram-project" class="me-1" />
          See all projects
        </b-button>
      </div>

      <!-- Recent Data Buckets -->
      <div :class="[{ 'd-none': !bucketListRef?.buckets.length }]">
        <h4 class="mt-4">
          <font-awesome-icon :icon="['fa', 'database']" />
          Recently added data
        </h4>
        <BucketList
          ref="bucketListRef"
          class="mb-1"
          parentName="Dashboard"
          :data-type="BUCKET_DATA_TYPES.INPUT"
          :created-after="limitDateTimeRecent"
          :paging-items-per-page="5"
          :paging-hide-controls="true"
          :show-user-role="false"
          :show-bucket-description="false"
          :message-no-items-found="'No new data were recently added to your collection.'"
          :transparent-style="true"
          :query-document="BucketsLiteDocument"
        />
        <b-button
          @click="openRouterLink({ name: 'DataManager' }, $router, $event)"
          variant="outline-dark"
          size="sm"
          class="mb-2"
        >
          <font-awesome-icon icon="database" class="me-1" />
          Go to Data Manager
        </b-button>
      </div>

      <!-- Recent data transfers -->
      <div :class="[{ 'd-none': !dataTransferListRef?.tasks.length }]">
        <h4 class="mt-4">
          <font-awesome-icon icon="arrow-right-arrow-left" class="me-1" />
          Recent data transfers
        </h4>
        <ComputeTaskList
          ref="dataTransferListRef"
          class="mb-1"
          parent-name="Dashboard"
          :user-role="SHARING_USER_ROLES.OWNER"
          :task-types="[
            COMPUTE_TASK_TYPES.FTP_TRANSFER,
            COMPUTE_TASK_TYPES.BASESPACE_TRANSFER,
          ]"
          :updated-after="limitDateTimeRecent"
          :paging-items-per-page="5"
          :paging-hide-controls="true"
          :show-exec-details="false"
          :show-abort-button="false"
          :message-no-items-found="'No recent data transfers found.'"
          :transparent-style="true"
          :query-document="ComputeTasksLiteDocument"
          task-name-column-title="Transfer"
        />
        <b-button
          @click="$router.push({ name: 'UploadManager' })"
          variant="outline-dark"
          size="sm"
          class="mb-2"
        >
          <font-awesome-icon icon="arrow-right-arrow-left" class="me-1" />
          See all data transfers
        </b-button>
      </div>
    </div>
    <!-- End of query section -->

    <!-- Recent uploads -->
    <div v-if="!uploadsStore.loading && uploadsStore.uploads.length > 0">
      <h4 class="mt-4">
        <font-awesome-icon icon="cloud-arrow-up" class="me-1" />
        Recent uploads
      </h4>
      <WebUploadList
        ref="webUploadListRef"
        class="mb-1"
        :transparent-style="true"
      />
      <b-button
        @click="openRouterLink({ name: 'UploadManager' }, $router, $event)"
        variant="outline-dark"
        size="sm"
        class="mb-2"
      >
        <font-awesome-icon icon="cloud-arrow-up" class="me-1" />
        See all uploads
      </b-button>
    </div>

    <!-- Upload new data instructions -->
    <h4 class="mt-4">
      <font-awesome-icon :icon="['fa', 'file-import']" />
      Import new data
    </h4>
    <p>
      Use one of the options below to import your FASTQ files and get them ready
      for analysis:
    </p>

    <ol>
      <li><strong>Upload FASTQs</strong> from your computer.</li>
      <li>Transfer files from an <strong>FTP server</strong>.</li>
      <li>
        Connect to your <strong>Illumina's BaseSpace Sequence Hub</strong> and
        select files for import.
      </li>
    </ol>

    <b-button
      @click="openRouterLink({ name: 'UploadManager' }, $router, $event)"
      variant="outline-dark"
      size="sm"
      class="mb-2"
    >
      <font-awesome-icon icon="file-import" class="me-1" />
      Import new data
    </b-button>

    <!-- Developer / Debug information -->
    <div v-if="IS_DEBUG_MODE || userStore.developer">
      <hr class="mt-4" />
      <h3 class="mt-4">
        <font-awesome-icon :icon="['fa', 'code']" />
        Developer Information
      </h3>
      <p v-if="!userStore.id" class="card-text">Loading...</p>
      <p v-else class="card-text">
        <strong>User ID:</strong> {{ userStore.id }}<br />
        <strong>Email:</strong> {{ userStore.email }}<br />
      </p>

      <!-- Authorization buttons -->
      <b-button-group
        class="btn-group btn-group-sm me-1 mb-1"
        role="group"
        aria-label="Authorization Buttons"
      >
        <b-button variant="outline-dark" disabled aria-disabled="true">
          <font-awesome-icon :icon="['fa', 'copy']" />
        </b-button>

        <b-button
          id="copy-button-uuid"
          variant="outline-dark"
          @click.prevent="copyUuid()"
        >
          UUID
        </b-button>

        <b-button
          id="copy-button-auth"
          variant="outline-dark"
          @click.prevent="copyAuth()"
        >
          Authorization header
        </b-button>
      </b-button-group>

      <!-- Tooltips for authorization buttons -->
      <b-tooltip
        v-if="showUuidTooltip"
        v-model="showUuidTooltip"
        ref="uuidTooltip"
        target="copy-button-uuid"
        placement="top"
      >
        UUID copied
      </b-tooltip>
      <b-tooltip
        v-if="showAuthTooltip"
        v-model="showAuthTooltip"
        ref="authTooltip"
        target="copy-button-auth"
        placement="top"
      >
        Authorization header copied
      </b-tooltip>

      <!-- button links to graphql playground, keycloak (open in new tab) -->
      <b-button
        variant="outline-dark"
        class="me-1 mb-1"
        size="sm"
        target="_blank"
        :href="GRAPHQL_ENDPOINT_URL"
      >
        <font-awesome-icon icon="play" class="me-1" />
        GraphQL Playground
      </b-button>

      <b-button
        variant="outline-dark"
        class="me-1 mb-1"
        size="sm"
        target="_blank"
        :href="KEYCLOAK_URL"
      >
        <font-awesome-icon icon="shield-halved" class="me-1" />
        IAM: Keycloak Console
      </b-button>

      <b-button
        variant="outline-dark"
        class="me-1 mb-1"
        size="sm"
        :href="keycloakAccountUrl"
        target="_blank"
      >
        <font-awesome-icon icon="user" class="me-1" />
        Keycloak Account
      </b-button>

      <b-button
        v-if="KEYCLOAK_URL.includes('localhost')"
        variant="outline-dark"
        class="me-1 mb-1"
        size="sm"
        target="_blank"
        href="http://localhost:8025"
      >
        <font-awesome-icon icon="envelope" class="me-1" />
        MailHog
      </b-button>

      <b-button
        v-if="KEYCLOAK_URL.includes('localhost')"
        variant="outline-dark"
        class="me-1 mb-1"
        size="sm"
        target="_blank"
        href="http://localhost:8081/?pgsql=backend-db&username=app_user&db=app_db"
      >
        <font-awesome-icon icon="database" class="me-1" />
        PostgreSQL Adminer
      </b-button>

      <!-- Admin Settings Override -->
      <b-button
        @click="userStore.admin = !userStore.admin"
        variant="outline-dark"
        class="me-1 mb-1"
        size="sm"
      >
        <font-awesome-icon
          :icon="userStore.admin ? 'user-slash' : 'user'"
          class="me-1"
        />
        {{ userStore.admin ? 'Disable' : 'Enable' }}
        Admin Settings
      </b-button>

      <!-- Developer Settings Override -->
      <b-button
        @click="userStore.developer = !userStore.developer"
        variant="outline-dark"
        class="me-1 mb-1"
        size="sm"
      >
        <font-awesome-icon
          :icon="userStore.developer ? 'bug-slash' : 'bug'"
          class="me-1"
        />
        {{ userStore.developer ? 'Disable' : 'Enable' }}
        Developer Settings
      </b-button>
    </div>
    <!-- End of Developer / Debug information -->

    <!-- Analytics Guide Modal -->
    <AnalyticsGuide
      v-if="showAnalyticsGuideModal"
      v-model:show="showAnalyticsGuideModal"
    />
  </div>
</template>
<style scoped>
.cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(325px, 1fr));
  gap: 1rem; /* similar to Bootstrap g-3 spacing */
  align-items: stretch;
}

.cards-grid__item {
  display: flex; /* lets the card stretch to full height */
}

.dashboard-card {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

/* keep footer at the bottom */
.dashboard-card .card-footer {
  margin-top: auto;
}

/* optional: unify image area */
.dashboard-card .card-img-top {
  aspect-ratio: 16 / 9;
  object-fit: cover;
}
</style>
